import { C as Component } from "./component-CZdU8SYg.js";
import { d as defineCustomElement } from "./utilities-D68o91ht.js";
class FacetsFormComponent extends Component {
  /** @type {AbortController | null} */
  #currentRequest = null;
  connectedCallback() {
    super.connectedCallback();
    this.sectionId = this.getAttribute("section-id");
    this.addEventListener("change", this.#handleChange, { signal: this.signal });
    window.addEventListener("popstate", this.#handlePopState, { signal: this.signal });
  }
  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener("change", this.#handleChange);
    window.removeEventListener("popstate", this.#handlePopState);
    if (this.#debounceTimer !== void 0) {
      clearTimeout(this.#debounceTimer);
      this.#debounceTimer = void 0;
    }
  }
  /**
   * Handles form input changes
   * @param {Event} event
   */
  #handleChange = (event) => {
    const form = this.querySelector("form");
    if (!form) {
      return;
    }
    const target =
      /** @type {HTMLElement} */
      event.target;
    if (target.closest("price-facet-component")) {
      clearTimeout(this.#debounceTimer);
      this.#debounceTimer = setTimeout(() => this.#submitForm(form), 500);
      return;
    }
    this.#submitForm(form);
  };
  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #debounceTimer;
  /**
   * Submits the form and fetches filtered results
   * @param {HTMLFormElement} form
   */
  #submitForm(form) {
    const formData = new FormData(form);
    const searchParams = new URLSearchParams();
    for (const [key, value] of formData.entries()) {
      if (value) {
        searchParams.append(
          key,
          /** @type {string} */
          value
        );
      }
    }
    const url = `${form.action}?${searchParams.toString()}`;
    this.#fetchAndRender(url);
  }
  /**
   * Handles browser back/forward navigation
   */
  #handlePopState = () => {
    this.#fetchAndRender(window.location.href, false);
  };
  /**
   * Fetches filtered content and updates the page
   * @param {string} url
   * @param {boolean} [updateHistory=true]
   */
  async #fetchAndRender(url, updateHistory = true) {
    if (this.#currentRequest) {
      this.#currentRequest.abort();
    }
    this.#currentRequest = new AbortController();
    this.#setLoadingState(true);
    try {
      const sectionUrl = new URL(url, window.location.origin);
      sectionUrl.searchParams.set("section_id", this.sectionId);
      const response = await fetch(sectionUrl.toString(), {
        signal: this.#currentRequest.signal,
      });
      if (!response.ok) {
        throw new Error("Network response was not ok");
      }
      const html = await response.text();
      if (updateHistory) {
        history.pushState({}, "", url);
      }
      this.#updateContent(html);
    } catch (error) {
      if (error.name !== "AbortError") {
        console.error("Facets fetch error:", error);
      }
    } finally {
      this.#setLoadingState(false);
      this.#currentRequest = null;
    }
  }
  /**
   * Updates the page content with new HTML
   * @param {string} html
   */
  #updateContent(html) {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, "text/html");
    const newGrid = doc.querySelector("[data-products-grid]");
    const currentGrid = document.querySelector("[data-products-grid]");
    if (newGrid && currentGrid) {
      currentGrid.innerHTML = newGrid.innerHTML;
    }
    const newInlineActiveFilters = doc.querySelector(
      ".active-filters--inline[data-active-filters]"
    );
    const currentInlineActiveFilters = document.querySelector(
      ".active-filters--inline[data-active-filters]"
    );
    const filterBarLeft = document.querySelector(".filter-bar__left");
    if (newInlineActiveFilters && currentInlineActiveFilters) {
      currentInlineActiveFilters.innerHTML = newInlineActiveFilters.innerHTML;
    } else if (newInlineActiveFilters && !currentInlineActiveFilters && filterBarLeft) {
      const filterButton = filterBarLeft.querySelector(".facets-toggle__button");
      if (filterButton) {
        filterButton.insertAdjacentHTML("afterend", newInlineActiveFilters.outerHTML);
      } else {
        filterBarLeft.insertAdjacentHTML("afterbegin", newInlineActiveFilters.outerHTML);
      }
    } else if (!newInlineActiveFilters && currentInlineActiveFilters) {
      currentInlineActiveFilters.remove();
    }
    const newDesktopActiveFilters = doc.querySelector(".filter-bar ~ [data-active-filters]");
    const currentDesktopActiveFilters = document.querySelector(
      ".filter-bar ~ [data-active-filters]"
    );
    const filterBar = document.querySelector(".filter-bar");
    if (newDesktopActiveFilters && currentDesktopActiveFilters) {
      currentDesktopActiveFilters.innerHTML = newDesktopActiveFilters.innerHTML;
    } else if (newDesktopActiveFilters && !currentDesktopActiveFilters && filterBar) {
      filterBar.insertAdjacentHTML("afterend", newDesktopActiveFilters.outerHTML);
    } else if (!newDesktopActiveFilters && currentDesktopActiveFilters) {
      currentDesktopActiveFilters.remove();
    }
    const newDrawerActiveFilters = doc.querySelector("#filter-drawer [data-active-filters]");
    const currentDrawerActiveFilters = document.querySelector(
      "#filter-drawer [data-active-filters]"
    );
    if (newDrawerActiveFilters && currentDrawerActiveFilters) {
      currentDrawerActiveFilters.innerHTML = newDrawerActiveFilters.innerHTML;
    }
    const newDrawerForm = doc.querySelector("#filter-drawer form");
    const currentDrawerForm = document.querySelector("#filter-drawer form");
    if (newDrawerForm && currentDrawerForm) {
      const newCheckboxes = newDrawerForm.querySelectorAll('input[type="checkbox"]');
      const currentCheckboxes = currentDrawerForm.querySelectorAll('input[type="checkbox"]');
      newCheckboxes.forEach((newCheckbox) => {
        const currentCheckbox = Array.from(currentCheckboxes).find(
          (cb) => cb.name === newCheckbox.name && cb.value === newCheckbox.value
        );
        if (currentCheckbox) {
          currentCheckbox.checked = newCheckbox.checked;
        }
      });
    }
    const allNewCounts = doc.querySelectorAll(".facets-toggle__count");
    const allCurrentCounts = document.querySelectorAll(".facets-toggle__count");
    if (allNewCounts.length === 0 && allCurrentCounts.length > 0) {
      allCurrentCounts.forEach((badge) => badge.remove());
    } else {
      allNewCounts.forEach((newCount2, index) => {
        if (allCurrentCounts[index]) {
          allCurrentCounts[index].textContent = newCount2.textContent;
        } else {
          const toggleButton = document.querySelectorAll(".facets-toggle__button")[index];
          const drawerTitle = document.querySelectorAll(".drawer-header__title")[index - 1];
          if (toggleButton) {
            toggleButton.insertAdjacentHTML("beforeend", newCount2.outerHTML);
          } else if (drawerTitle) {
            drawerTitle.insertAdjacentHTML("beforeend", newCount2.outerHTML);
          }
        }
      });
    }
    const newCount = doc.querySelector("[data-products-count]");
    const currentCount = document.querySelector("[data-products-count]");
    if (newCount && currentCount) {
      currentCount.textContent = newCount.textContent;
    }
    const newPagination = doc.querySelector("[data-pagination]");
    const currentPagination = document.querySelector("[data-pagination]");
    if (newPagination && currentPagination) {
      currentPagination.innerHTML = newPagination.innerHTML;
    }
    this.#upgradeCustomElements();
  }
  /**
   * Upgrade all custom elements after innerHTML swap
   * Ensures facet-remove-component and other custom elements are properly initialized
   */
  #upgradeCustomElements() {
    const selectors = ["facet-remove-component", "facets-form-component", "price-facet-component"];
    const elements = this.querySelectorAll(selectors.join(","));
    elements.forEach((element) => {
      window.customElements?.upgrade(element);
    });
  }
  /**
   * Sets loading state on the product grid
   * @param {boolean} isLoading
   */
  #setLoadingState(isLoading) {
    const grid = document.querySelector("[data-products-grid]");
    if (grid) {
      grid.classList.toggle("opacity-50", isLoading);
      grid.classList.toggle("pointer-events-none", isLoading);
    }
  }
  /**
   * Clears all filters
   * @param {string} [url] - URL to navigate to (defaults to collection URL without filters)
   */
  clearAll(url) {
    const clearUrl = url || window.location.pathname;
    this.#fetchAndRender(clearUrl);
  }
}
class PriceFacetComponent extends Component {
  connectedCallback() {
    super.connectedCallback();
    this.minInput = this.querySelector('input[name*="filter.v.price.gte"]');
    this.maxInput = this.querySelector('input[name*="filter.v.price.lte"]');
    if (this.minInput) {
      this.minInput.addEventListener("input", this.#handleInput);
    }
    if (this.maxInput) {
      this.maxInput.addEventListener("input", this.#handleInput);
    }
  }
  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.minInput) {
      this.minInput.removeEventListener("input", this.#handleInput);
    }
    if (this.maxInput) {
      this.maxInput.removeEventListener("input", this.#handleInput);
    }
  }
  /**
   * Validates and formats price input
   * @param {Event} event
   */
  #handleInput = (event) => {
    const input =
      /** @type {HTMLInputElement} */
      event.target;
    input.value = input.value.replace(/[^0-9.]/g, "");
  };
}
class FacetRemoveComponent extends Component {
  connectedCallback() {
    super.connectedCallback();
    this.addEventListener("click", this.#handleClick);
  }
  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener("click", this.#handleClick);
  }
  #handleClick = (event) => {
    event.preventDefault();
    const url = this.getAttribute("href");
    if (!url) {
      return;
    }
    let facetsForm = this.closest("facets-form-component");
    if (!facetsForm) {
      facetsForm = document.querySelector("facets-form-component");
    }
    if (facetsForm instanceof FacetsFormComponent) {
      facetsForm.clearAll(url);
    }
  };
}
defineCustomElement("facets-form-component", FacetsFormComponent);
defineCustomElement("price-facet-component", PriceFacetComponent);
defineCustomElement("facet-remove-component", FacetRemoveComponent);
